import { AlertTriangle, RefreshCw } from 'lucide-react';
import { toApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  className?: string;
}

/**
 * Failure panel for a view that could not load.
 *
 * The `requestId` is surfaced on purpose — it is the one thing that lets
 * support find the matching server log, and asking a user "what time was it?"
 * almost never works.
 */
export function ErrorState({ error, onRetry, title, className }: ErrorStateProps) {
  const apiError = toApiError(error);

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-6 py-12 text-center',
        className,
      )}
    >
      <div className="flex size-11 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle className="size-5 text-destructive" aria-hidden="true" />
      </div>

      <div className="space-y-1">
        <p className="font-medium">{title ?? 'Could not load this'}</p>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">{apiError.message}</p>
      </div>

      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw aria-hidden="true" />
          Try again
        </Button>
      )}

      {apiError.requestId && (
        <p className="font-mono text-[11px] text-muted-foreground">
          Reference: {apiError.requestId}
        </p>
      )}
    </div>
  );
}
